import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import { FormControl } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';

export class CalendarItem {
  date: Date;
  format: string;
  disabled: boolean;
  active: boolean;
  selected: boolean;
  constructor(date: Date, format: string, disabled = false, active = false, selected = false) {
    this.date = date;
    this.format = format;
    this.disabled = disabled;
    this.active = active;
    this.selected = selected;
  }
}

export class CalendarDay extends CalendarItem {
  events?: any[];
  constructor(date: Date, disabled?: boolean, active?: boolean, selected?: boolean) {
    super(date, 'd', disabled, active, selected);
  }
}

export class CalendarMonth extends CalendarItem {
  constructor(date: Date, active?: boolean, selected?: boolean) {
    super(date, 'MMM', false, active, selected);
  }
}

export type CalendarView = 'month'|'year'|'week';

@Component({
  selector: 'md-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent extends AbstractFormControl implements OnInit {

  @Input() public formModel?: FormControl;
  @Input() public tabindex = 0;
  @Input() public multiselect: boolean = false;
  @Input() public selected: Date | Date[] | null = null;
  @Output() public selectedChanged: EventEmitter<Date | Date[] | null> = new EventEmitter();

  public view: CalendarView = 'month';
  public viewDate: Date = new Date();
  public calendarItems: CalendarDay[] | CalendarMonth [] = [];

  static isSameDay(first: Date, second: Date): boolean {
    try {
      if (first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate()) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  static isSameMonth(first: Date, second: Date): boolean {
    try {
      if (first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth()) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  constructor() {
    super();
  }

  ngOnInit() {
    if (this.formModel) {
      this.selected = this.formModel.value;

      this.formModel.valueChanges.pipe(
        takeUntil(this.destroyed)
      ).subscribe(value => {
        this.selected = value;
        this.updateCalendarItems();
      })
    }

    if (!this.multiselect && this.selected) {
      this.viewDate = new Date(this.selected as Date);
    } else {
      this.viewDate = new Date();
    }
    this.viewDate.setDate(1);
    this.updateCalendarView('month');
  }

  public changeMonth(event: MouseEvent): void {
    event.stopPropagation();
    this.updateCalendarView('year');
  }

  public prevMonth(event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate.setMonth(this.viewDate.getMonth() - 1);
    this.updateCalendarView('month');
  }

  public nextMonth(event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate.setMonth(this.viewDate.getMonth() + 1);
    this.updateCalendarView('month');
  }

  public prevYear(event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate.setFullYear(this.viewDate.getFullYear() - 1);
    this.updateCalendarView('year');
  }

  public nextYear(event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate.setFullYear(this.viewDate.getFullYear() + 1);
    this.updateCalendarView('year');
  }

  public selectDay(day: CalendarDay): void {
    if (this.multiselect) {
      if (day.selected && Array.isArray(this.selected)) {
        const dateIndex = this.selected.findIndex(d => CalendarComponent.isSameDay(d, day.date));
        if (dateIndex > -1) {
          this.selected.splice(dateIndex, 1);
        }
      } else {
        if (Array.isArray(this.selected)) {
          this.selected.push(day.date);
        } else {
          this.selected = [day.date];
        }
      }

      if (!this.selected.length) {
        this.selected = null;
      }

    } else {
      if (day.selected) {
        this.selected = null;
      } else {
        this.selected = day.date;
      }
    }

    this.updateCalendarItems();

    if (this.formModel) {
      this.formModel.markAsDirty();
      this.formModel.setValue(this.selected);
    }
    this.selectedChanged.emit(this.selected);
  }

  public selectMonth(month: CalendarMonth): void {
    this.viewDate = month.date;
    this.updateCalendarView('month');
  }

  protected updateCalendarView(view: CalendarView): void {
    this.view = view;
    this.viewDate = new Date(this.viewDate);
    if (view === 'year') {
      this.calendarItems = this.getCalendarMonths();
    } else if (view === 'month') {
      this.calendarItems = this.getCalendarDays();
    }
  }

  protected getCalendarDays(): CalendarDay[] {
    const days: CalendarDay[] = [];

    const date = new Date(this.viewDate);
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    date.setDate(1 - date.getDay());

    while (days.length < (6 * 7)) {
      const isDisabled: boolean = date.getMonth() !== this.viewDate.getMonth();
      const isActive: boolean = CalendarComponent.isSameDay(date, new Date());

      let isSelected = false;
      if (Array.isArray(this.selected)) {
        isSelected = this.selected.some(d => CalendarComponent.isSameDay(date, d));
      } else if (this.selected) {
        isSelected = CalendarComponent.isSameDay(date, this.selected);
      }

      const cDay = new CalendarDay(new Date(date), isDisabled, isActive, isSelected);
      days.push(cDay);
      date.setDate(date.getDate() + 1);
    }

    return days;
  }

  protected getCalendarMonths(): CalendarMonth[] {
    const months: CalendarMonth[] = [];
    const date = new Date(this.viewDate);
    date.setDate(1);
    date.setMonth(0);

    while (months.length < 12) {
      const isActive: boolean = CalendarComponent.isSameMonth(date, new Date());
      let isSelected = false;
      if (Array.isArray(this.selected)) {
        isSelected = this.selected.some(d => CalendarComponent.isSameMonth(date, d));
      } else if (this.selected) {
        isSelected = CalendarComponent.isSameMonth(date, this.selected);
      }
      const cMonth = new CalendarMonth(new Date(date), isActive, isSelected);
      months.push(cMonth);
      date.setMonth(date.getMonth() + 1);
    }
    return months;
  }

  private updateCalendarItems(): void {
    if (Array.isArray(this.selected)) {
      this.calendarItems.forEach(item => {
        item.selected = (<Array<Date>>this.selected).some(date => CalendarComponent.isSameDay(date, item.date));
      });
    } else if (this.selected) {
      this.calendarItems.forEach(item => {
        item.selected = CalendarComponent.isSameDay(item.date, this.selected as Date);
      });
    } else {
      this.calendarItems.forEach(item => item.selected = false);
    }
  }
}
